import { useParams } from 'react-router-dom';
import { useGetAccountByIdQuery } from '@/store/api/accountsApi';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BackButton } from '@/components/common/BackButton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings } from 'lucide-react';

export function UserDetailPage() {
  const { id } = useParams();

  // safer parsing
  const accountId = Number(id);
  const isValidId = !isNaN(accountId);

  const { data, isLoading, isError } = useGetAccountByIdQuery(accountId, {
    skip: !isValidId,
  });

  // loading
  if (isLoading) {
    return <div className="p-6">Loading user details...</div>;
  }

  // error
  if (isError || !data) {
    return <div className="p-6 text-red-500">Failed to load user details</div>;
  }

  // destructure according to REAL API
  const {
    id: userId,
    firstname,
    lastname,
    username,
    email,
    mobile,
    status,
    role,
    isVerified,
    createdAt,
    profileImage,
  } = data;

  // safe full name
  const fullName = `${firstname ?? ''} ${lastname ?? ''}`.trim() || username || 'Unknown User';

  // safe initial
  const initial = firstname?.charAt(0)?.toUpperCase() || username?.charAt(0)?.toUpperCase() || 'U';

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center gap-5 mb-4">
        <BackButton />

        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full overflow-hidden flex items-center justify-center bg-primary text-primary-foreground text-lg font-semibold">
            {profileImage ? (
              <img
                src={profileImage}
                alt={fullName}
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              initial
            )}
          </div>

          <div>
            <h2 className="text-2xl font-bold">{fullName}</h2>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="basicDetails" className="space-y-4 w-full">
        {/* <TabsList className="w-full">
          <TabsTrigger value="basicDetails" className="flex items-center px-4">
            <Settings className="w-4 h-4 mr-1" />
            Basic Details
          </TabsTrigger>
        </TabsList> */}

        {/* BASIC DETAILS TAB */}
        <TabsContent value="basicDetails" className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>

            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <strong>Mobile:</strong> {mobile ?? '—'}
              </div>

              <div>
                <strong>Email:</strong> {email ?? '—'}
              </div>

              <div>
                <strong>Role:</strong> <Badge variant="secondary">{role ?? '—'}</Badge>
              </div>

              <div>
                <strong>Status:</strong>{' '}
                <Badge variant={status === 'Active' ? 'default' : 'secondary'}>
                  {status ?? '—'}
                </Badge>
              </div>

              <div>
                <strong>Joined On:</strong> {createdAt ? new Date(createdAt).toLocaleString() : '—'}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
